import React from 'react';

/**
 * Renders a compact 4-row "Exploitation Status" table on the EUVD detail page.
 *
 * Every row reads the same way — Source (icon + name) · Status (headline value
 * or a "missing" fallback) · Details (main metric line(s) + a grey source
 * description). Rows (read top-down: forecast → curated → observed):
 *   1. EPSS Forecast            — predictive probability from FIRST (epss, 0–100)
 *   2. CISA KEV                 — kevEntries[].kevSource.code === "CISA"
 *   3. EU KEV                   — kevEntries[].kevSource.code === "EUKEV"
 *   4. Honeypot sensors         — honeypotObservations[0] (Shadowserver)
 *
 * The whole section is hidden only when NONE of the four signals apply.
 */
export default function ExploitationInsightsTable({ kevEntries = [], honeypotObservations = [], epss = 0 }) {
  const cisa = pickKev(kevEntries, 'CISA');
  const eukev = pickKev(kevEntries, 'EUKEV');
  const honeypot = honeypotObservations.length > 0 ? honeypotObservations[0] : null;
  const showEpss = typeof epss === 'number' && epss > 0;

  const hasAny = !!cisa || !!eukev || !!honeypot || showEpss;
  if (!hasAny) return null;

  return (
    <section className="exploitation-insights">
      <h3 className="underlined-title">Exploitation Status</h3>
      <table className="exploitation-insights-table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Status</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <EpssRow epss={epss} />
          <KevRow label="CISA KEV" description="CISA Known Exploited Vulnerabilities Catalog" entry={cisa} variant="cisa" />
          <KevRow label="EU KEV" description="EU Known Exploited Vulnerabilities" entry={eukev} variant="eukev" />
          <HoneypotRow obs={honeypot} />
        </tbody>
      </table>
    </section>
  );
}

function EpssRow({ epss }) {
  const isMissing = !(typeof epss === 'number' && epss > 0);
  return (
    <tr className="insights-row insights-row-epss">
      <td className="insights-source">
        <span className="insights-icon insights-icon-epss" aria-hidden="true"></span>
        EPSS Forecast
      </td>
      <td className="insights-status" data-header="Status">
        {isMissing
          ? <span className="insights-empty">Not yet available</span>
          : <strong>{epss.toFixed(2)}%</strong>}
      </td>
      <td className="insights-details" data-header="Details">
        <div>Probability of exploitation in the wild in the next 30 days</div>
        <div className="insights-source-desc">Exploit Prediction Scoring System from FIRST</div>
      </td>
    </tr>
  );
}

function pickKev(kevEntries, code) {
  return kevEntries.find(e => e.kevSource && e.kevSource.code === code) || null;
}

function KevRow({ label, description, entry, variant }) {
  return (
    <tr className={`insights-row insights-row-${variant}`}>
      <td className="insights-source">
        <span className={`insights-icon insights-icon-kev insights-icon-${variant}`} aria-hidden="true"></span>
        {label}
      </td>
      <td className="insights-status" data-header="Status">
        {entry
          ? <span>Added <strong>{formatDate(entry.dateAdded)}</strong></span>
          : <span className="insights-empty">Not listed</span>}
      </td>
      <td className="insights-details" data-header="Details">
        <div className="insights-source-desc">{description}</div>
      </td>
    </tr>
  );
}

function HoneypotRow({ obs }) {
  return (
    <tr className="insights-row insights-row-honeypot">
      <td className="insights-source">
        <span className="insights-icon insights-icon-honeypot" aria-hidden="true"></span>
        Honeypot sensors
      </td>
      <td className="insights-status" data-header="Status">
        {obs ? <HoneypotTrend obs={obs} /> : <span className="insights-empty">No data</span>}
      </td>
      <td className="insights-details" data-header="Details">
        {obs && <HoneypotDetails obs={obs} />}
        <div className="insights-source-desc">Exploited vulnerabilities - Anomalies (Shadowserver)</div>
      </td>
    </tr>
  );
}

function HoneypotTrend({ obs }) {
  const ratio = computeRatio(obs);
  const trend = obs.trend || 'STEADY';
  return (
    <>
      {obs.firstSeenAt && (
        <div className="insights-first-seen">First seen {formatDate(obs.firstSeenAt)}</div>
      )}
      <span className="insights-trend-line">
        Trend:{' '}
        <span className={`insights-trend insights-trend-${trend.toLowerCase()}`}>
          {trendArrow(trend)} {trend}
        </span>
        {ratio !== null && (
          <span className="insights-ratio"> ({ratio}× vs 7-day avg)</span>
        )}
      </span>
    </>
  );
}

function HoneypotDetails({ obs }) {
  return (
    <div className="insights-honeypot-detail">
      <div>
        <strong>{fmt(obs.connections1d)}</strong> connections &middot;{' '}
        <strong>{fmt(obs.uniqueIps1d)}</strong> unique IPs <em>/ 24h</em>
      </div>
      <div className="insights-averages">
        7d avg: <strong>{fmt(obs.avg7d)}</strong>{' '}
        &middot; 30d avg: <strong>{fmt(obs.avg30d)}</strong>{' '}
        &middot; 90d avg: <strong>{fmt(obs.avg90d)}</strong>
      </div>
    </div>
  );
}

function trendArrow(trend) {
  if (trend === 'UPTICK') return '↑';
  if (trend === 'DECLINE') return '↓';
  return '→';
}

function computeRatio(obs) {
  if (!obs.connections1d || !obs.avg7d) return null;
  const avg = parseFloat(obs.avg7d);
  if (!avg || avg <= 0) return null;
  return (obs.connections1d / avg).toFixed(1);
}

function fmt(n) {
  if (n === null || n === undefined) return '-';
  if (typeof n === 'number') return n.toLocaleString();
  return n;
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
